import { useRouter } from 'next/router'
import { useState, useCallback, useEffect } from 'react'
import { captureMessage, captureException } from '@sentry/nextjs'
import { differenceInSeconds, format } from 'date-fns'

import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'
import playcanvasConfig from '~/config/playcanvas'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'
import { useGetPetStateQuery } from '~/shared/apis/pet-state.api'
import {
  useSubmitRoundMutation,
  useLazyGetHighScoresQuery,
  useLazyGetMultGameStatsQuery,
  useLazyGetWorkoutQuestionsQuery,
  useUpdateWorkoutQuestionsMutation,
  useCreateWorkoutQuestionsMutation,
} from '~/games/apis/multgame-api'
import { DataRoutes, DataTypes } from '~/games/constants/multgame.const'
import { TPlaycanvasRequest, TStat, StatTypes, TStatType } from '~/games/types/multgame.types'
import ROUTES from '~/shared/constants/routes.const'
import { Language } from '~/../utils/sitemaps/types'
import { getSubdomain } from '~/utils/domain'
import { useSetRewardsMutation } from '~/redux/apis/reward-api'
import { calculateGemCount } from '~/utils/helpers'
import { REWARD_EVENT, REWARD_FEATURE } from '~/gamification/types/gamification.types'

function prepareStatsForChart(statType: TStatType, stats: TStat[]): Array<[number, string]> {
  if (stats && stats.length > 0) {
    return stats.map((stat) => {
      const date = format(new Date(stat.endTime), 'yyyy-MM-dd')
      if (statType === StatTypes.ACCURACY) {
        return [stat.accuracy as number, date]
      } else if (statType === StatTypes.SCORE) {
        return [stat.score as number, date]
      } else if (statType === StatTypes.TIME_TAKEN) {
        return [differenceInSeconds(new Date(stat.endTime), new Date(stat.startTime)), date]
      }

      return [-1, date]
    })
  }

  return []
}

function useMultGame(): { multGameURL: string } {
  const [multIframe, setMultIframe] = useState<HTMLIFrameElement | undefined>(undefined)
  const [isReady, setIsReady] = useState(false)
  const user = useCurrentUser()
  const { data: avatarMetadata } = useGetAvatarMetadataQuery(user, { skip: !user?.avatarUrl })
  const { data: petState } = useGetPetStateQuery(user?.id as number, { skip: !user?.id })
  const [setRewards] = useSetRewardsMutation()
  const [submitRound] = useSubmitRoundMutation()
  const [getHighScores] = useLazyGetHighScoresQuery()
  const [getStats] = useLazyGetMultGameStatsQuery()
  const [getWorkouts] = useLazyGetWorkoutQuestionsQuery()
  const [updateWorkout] = useUpdateWorkoutQuestionsMutation()
  const [createWorkout] = useCreateWorkoutQuestionsMutation()

  const router = useRouter()
  const language = router.locale
  const { menuPage } = router.query

  const isMenuPage = menuPage === 'true'

  const urlLanguage = language !== Language.EN ? `/${language}` : ''
  const subdomain = getSubdomain()
  const domain = subdomain.includes('houseofmath.com') ? subdomain : `${subdomain}.houseofmath.com`

  const BootcampMultiplicationURL = `https://${domain}${urlLanguage}${ROUTES.BOOTCAMP_MULTIPLICATION}`

  const sendMessageToIframe = useCallback(
    (data: unknown) => {
      const postMessage = multIframe?.contentWindow

      if (postMessage) {
        postMessage.postMessage(data, playcanvasConfig.multGameURL.origin)
      }
    },
    [multIframe],
  )

  useEffect(() => {
    setMultIframe(document.getElementById('multIframe') as HTMLIFrameElement)
  }, [])

  useEffect(
    function initialize() {
      const postMessage = multIframe?.contentWindow
      if (isReady && postMessage) {
        if (user && language && petState) {
          const initialData = {
            type: DataTypes.INITIAL_DATA,
            user: {
              avatar: avatarMetadata ? avatarMetadata : null,
            },
            petState,
            language,
            menuPage: Boolean(isMenuPage),
          }
          sendMessageToIframe(initialData)
        }
      }
    },
    [isReady, multIframe, user, avatarMetadata, language, petState, sendMessageToIframe, isMenuPage],
  )

  const requestHandler = useCallback(
    async (eventData: TPlaycanvasRequest) => {
      let response
      let stats
      const { type, dataType, data } = eventData
      switch (dataType) {
        case DataTypes.ROUND:
          response = await submitRound({ roundData: data.roundData, highScores: data.highScores }).unwrap()
          if (data.reward?.points || data.reward?.gemCount) {
            await setRewards({
              xp: data.reward.points,
              gems: calculateGemCount(data.reward.gemType, data.reward.gemCount),
              feature: REWARD_FEATURE.GAME_MULTGAME,
              event_id: `${REWARD_FEATURE.GAME_MULTGAME}#${REWARD_EVENT.ROUND_CREATED}`,
            }).unwrap()
          }

          break
        case DataTypes.HIGH_SCORES:
          response = await getHighScores().unwrap()
          break
        case DataTypes.GAME_STATS:
          stats = await getStats(data).unwrap()
          response = prepareStatsForChart(data.statType, stats)
          break

        case DataTypes.WORKOUT_CREATE:
          response = await createWorkout(data).unwrap()
          break

        case DataTypes.WORKOUT_UPDATE:
          response = await updateWorkout(data).unwrap()
          break

        case DataTypes.WORKOUT_READ:
          response = await getWorkouts().unwrap()
          break

        default:
          response = { type: 'error' }
          captureMessage(`Unknown request dataType from multgame: ${dataType}`)
      }

      sendMessageToIframe({ type, dataType, data, response })
    },
    [sendMessageToIframe, createWorkout, getHighScores, getStats, getWorkouts, setRewards, submitRound, updateWorkout],
  )

  const messageHandler = useCallback(
    async (event) => {
      if (event.origin === playcanvasConfig.multGameURL.origin) {
        try {
          const data = event.data
          switch (data.type) {
            case DataTypes.REQUEST:
              await requestHandler(data)
              break
            case DataTypes.INITIAL_DATA:
              setIsReady(true)
              break
            case DataTypes.PLAYCANVAS_ANALYTICS_EVENT:
              playCanvasAnalytics.logPlayCanvasEvent(data)
              break
            case DataTypes.OPEN_PAGE:
              if (data.to === DataRoutes.BOOTCAMP) window.open(BootcampMultiplicationURL)
              break
            case DataTypes.ROUTE:
              if (data.to === DataRoutes.GAMES) router.push(ROUTES.GAMES)
              else if (data.to === DataRoutes.DASHBOARD) router.push(ROUTES.DASHBOARD)
              break
            case DataTypes.MAKE_AVATAR:
              router.replace(`${ROUTES.AVATAR}?from=multgame`)
              break

            default:
              captureMessage('Unknown event type from multgame')
          }
        } catch (error) {
          sendMessageToIframe({ ...event.data, data: { type: 'error' } })
          captureException(error, { tags: { feature: 'games', action: 'multgame-error' } })
        }
      }
    },
    [sendMessageToIframe, requestHandler, BootcampMultiplicationURL, router],
  )

  useEffect(() => {
    window.addEventListener('message', messageHandler)

    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler])

  return {
    multGameURL: playcanvasConfig.multGameURL.href,
  }
}

export default useMultGame
